import { AlertTriangle, Minus, Plus, RefreshCw } from 'lucide-react';
import { useTranslation } from '@/i18n';
import { useErrorTranslator } from '@/errors/translate';
import type { ImportResult, ImportRowOutcome, ImportRowResult } from '@/types/excelImport';
import { Badge } from './Badge';

/**
 * Read-only renderer for an `ImportResult` (dry-run preview or commit report).
 *
 * A trimmed sibling of `ExcelImportModal`'s in-modal report: same outcome vocabulary and
 * badge styling, but no row selection — the Netzplan wizard curates rows in its own steps,
 * so here the result only needs to be shown (failures first).
 */

const OUTCOME_ORDER: ImportRowOutcome[] = ['Failed', 'Created', 'Updated', 'Skipped'];

const OUTCOME_BADGE: Record<ImportRowOutcome, 'success' | 'info' | 'default' | 'danger'> = {
  Created: 'success',
  Updated: 'info',
  Skipped: 'default',
  Failed: 'danger',
};

const OUTCOME_ICON: Record<ImportRowOutcome, typeof Plus> = {
  Created: Plus,
  Updated: RefreshCw,
  Skipped: Minus,
  Failed: AlertTriangle,
};

function OutcomeBadge({ outcome, label }: { outcome: ImportRowOutcome; label: string }) {
  const Icon = OUTCOME_ICON[outcome];
  return (
    <Badge variant={OUTCOME_BADGE[outcome] ?? 'default'}>
      {Icon && <Icon className="w-3 h-3 mr-1" strokeWidth={2} />}
      {label}
    </Badge>
  );
}

function countOf(result: ImportResult, outcome: ImportRowOutcome): number {
  switch (outcome) {
    case 'Created': return result.created;
    case 'Updated': return result.updated;
    case 'Skipped': return result.skipped;
    case 'Failed': return result.failed;
  }
}

interface Props {
  result: ImportResult;
  /** When true, only failed rows are listed (keeps a large happy-path result compact). */
  failuresOnly?: boolean;
}

export function ImportResultView({ result, failuresOnly = true }: Props) {
  const { t } = useTranslation(['common', 'errors']);
  const translateError = useErrorTranslator();

  const rows = [...result.rows].sort(
    (a, b) => OUTCOME_ORDER.indexOf(a.outcome) - OUTCOME_ORDER.indexOf(b.outcome) || a.row - b.row,
  );
  const shown = failuresOnly ? rows.filter((r) => r.outcome === 'Failed') : rows;

  const reason = (row: ImportRowResult): string => {
    const text = row.reasonCode
      ? translateError({ code: row.reasonCode, message: row.reasonMessage ?? '' })
      : (row.reasonMessage ?? '');
    return row.column && text ? `${row.column}: ${text}` : text;
  };

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap gap-2">
        {OUTCOME_ORDER.map((o) => (
          <span
            key={o}
            className={`inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[12px] ${
              o === 'Failed' && countOf(result, o) > 0 ? 'border-red-200 text-red-600' : 'border-slate-200 text-slate-600'
            }`}
          >
            {t(`common:excelImport.outcomes.${o}`)}
            <span className="font-mono tabular-nums text-[11px]">{countOf(result, o)}</span>
          </span>
        ))}
      </div>

      {shown.length > 0 && (
        <div className="rounded-xl border border-slate-200 overflow-hidden">
          <div className="max-h-[38vh] overflow-y-auto divide-y divide-slate-100">
            {shown.map((row) => (
              <div
                key={`${row.sheet}:${row.row}`}
                className="grid grid-cols-[48px_minmax(0,1.4fr)_104px_minmax(0,1.4fr)] gap-3 px-3 py-2 items-center"
              >
                <span className="text-[12px] text-slate-500 tabular-nums">{row.row}</span>
                <span className="text-[13px] text-slate-900 truncate">{row.label || '—'}</span>
                <OutcomeBadge
                  outcome={row.outcome}
                  label={t(`common:excelImport.outcomes.${row.outcome}`, { defaultValue: row.outcome })}
                />
                <span className="text-[12px] text-slate-600 truncate">{reason(row) || '—'}</span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export default ImportResultView;
